/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import useApi from 'hooks/useApi';
import { showAlert } from 'redux/actions/alertActions';

import { Switch } from 'shared/components/form/Inputs';
import { colors } from 'shared/defaultTheme';
import { Checkbox } from 'shared/components/form/CheckBox';
import { Button } from 'shared/components/Buttons';
import AlertModal from 'shared/components/AlertModal';
import { ALERT_TYPE_ERROR } from 'shared/utils';

import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';

import { ShareDocumentContactsWrapper, SearchContacts } from './styles';

const ShareDocumentContacts = ({
  selectedClients,
  setSelectedClients,
  selectedFiles,
  setSelectedFiles,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { customAreaApi, ourRequest } = useApi();
  const { cusNavId, mail } = useSelector((st) => st.auth);

  const [selectedContacts, setSelectedContacts] = useState([]);
  const [allSelectedClients, setAllSelectedClients] = useState([]);
  const [showWaiting, setShowWaiting] = useState(false);
  const [WaitingMessage, setWaitingMessage] = useState('');
  const [showSuccessUpload, setShowSuccessUpload] = useState(false);
  const codeOperation = useRef(null);
  const showCancelOperation = useRef(false);

  const [, getContacts] = customAreaApi(
    {
      url: `${window.API_V1}/cli/contacts/${cusNavId}/search/all`,
      method: 'POST',
      data: {
        filters: [
          {
            model: 'Contact',
            field: 'Name',
            asc: true,
          },
        ],
        reduce: {
          limit: 0,
          skip: 0,
        },
      },
    },
    { manual: true }
  );

  const [, createDocument] = customAreaApi(
    {
      method: 'POST',
      cancelToken: ourRequest.token,
    },
    { manual: true }
  );

  const [, startOperation] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const [, cancelOperation] = customAreaApi(
    {
      method: 'PUT',
    },
    { manual: true }
  );

  const onCancelOperation = async () => {
    setShowWaiting(false);
    showCancelOperation.current = true;
    const {
      data: { data },
    } = await cancelOperation({
      url: `${window.API_V1}/ope/${cusNavId}/${encodeURIComponent(mail)}/${
        codeOperation.current
      }?type=copy`,
    });
    if (data) {
      codeOperation.current = null;
      onClose();
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_ERROR,
          alertMessage: t('custom_field_operation_error_message'),
        })
      );
    }
  };

  const removeSelectedContacts = (SelectedMail) => {
    const newContacts = selectedContacts.filter((x) => x.mail !== SelectedMail);
    setSelectedContacts(newContacts);
  };

  const selectedContact = useCallback((e, contact) => {
    const existingContact = selectedContacts.some((x) => x.mail === contact.mail);
    if (!existingContact) {
      const newContacts = [...selectedContacts, contact];
      setSelectedContacts(newContacts);
    } else {
      removeSelectedContacts(contact.mail);
    }
  });

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const {
          data: { data },
        } = await getContacts();
        const filterContacts = data.filter((contact) => contact.mail);
        let newSelectedContacts = [];
        const newSelectClients = selectedClients.map((selectedClient) => {
          const findContacts = filterContacts.filter(
            (contact) => contact.clientId === selectedClient.clientId
          );
          newSelectedContacts = [...newSelectedContacts, ...findContacts];
          return { ...selectedClient, contacts: findContacts };
        });
        setSelectedClients(newSelectClients);
        setSelectedContacts(newSelectedContacts);
        setAllSelectedClients(newSelectClients);
      } catch (error) {
        console.error(`Error Contacts: ${error}`);
      }
    };
    fetchContacts();
  }, []);

  const onSearchContacts = async (e, data) => {
    const { value } = e.target;
    const lowerSearchText = value?.toLowerCase() ?? '';
    let contactResults = [];
    if (lowerSearchText.length > 0) {
      contactResults = data.contacts.filter((contact) => {
        return (
          (contact.name ?? '').toLowerCase().includes(lowerSearchText) ||
          (contact.surname ?? '').toLowerCase().includes(lowerSearchText)
        );
      });
      const newSelectedClients = selectedClients.map((selectedClient) => {
        if (selectedClient.clientId === data.id) {
          return { ...selectedClient, contacts: contactResults };
        }
        return { ...selectedClient };
      });
      setSelectedClients(newSelectedClients);
    }
    if (!lowerSearchText.length) {
      setSelectedClients([...allSelectedClients]);
    }
  };

  const onChangeActiveContactAll = (checked, value) => {
    const newSelectClients = selectedClients.map((selectedClient) => {
      if (selectedClient.clientId === value.clientId) {
        return { ...selectedClient, isActiveAll: checked };
      }
      return { ...selectedClient };
    });
    if (checked) {
      const selectedClientsFilter = selectedClients.find((x) => x.clientId === value.clientId);
      setSelectedContacts([...selectedContacts, ...selectedClientsFilter.contacts]);
    } else {
      const filterContacts = selectedContacts.filter((x) => x.clientId !== value.clientId);
      setSelectedContacts(filterContacts);
    }
    setSelectedClients(newSelectClients);
  };

  const onCreateDocument = async () => {
    const {
      data: { data },
    } = await startOperation({
      url: `${window.API_V1}/ope/${cusNavId}/${encodeURIComponent(mail)}?type=copy`,
    });
    codeOperation.current = data.code;
    showCancelOperation.current = false;
    setShowWaiting(true);
    try {
      for (let index = 0; index < selectedClients.length; index += 1) {
        if (showCancelOperation.current) break;
        const selectedClient = selectedClients[index];
        const filterContacts = selectedContacts.filter(
          (x) => x.clientId === selectedClient.clientId
        );
        const permissions = filterContacts.map((permission) => {
          return { u: permission.mail, p: 'r--' };
        });
        const newData = selectedFiles.map((selectedFile) => {
          return { ...selectedFile, permissions: [...permissions] };
        });
        setWaitingMessage(
          t('documents_upload_document_client', {
            value: selectedClient.clientName,
          })
        );
        // eslint-disable-next-line no-await-in-loop
        await createDocument({
          data: newData,
          url: `${window.API_V1}/cli/items/${cusNavId}/${
            selectedClient.clientId
          }/${encodeURIComponent(mail)}?publish=${true}&codeOperation=${data.code}`,
        });
      }
      setShowWaiting(false);
      setShowSuccessUpload(true);
      setSelectedFiles([]);
    } catch (error) {
      setShowWaiting(false);
      if (!showCancelOperation.current) {
        dispatch(
          showAlert({
            alertType: ALERT_TYPE_ERROR,
            alertMessage: t('custom_field_operation_error_message'),
          })
        );
      }
    }
  };

  const onOkSuccessUpload = () => {
    setShowSuccessUpload(false);
    onClose();
  };

  const Title = () => {
    return (
      <Row className="row-title">
        <Col sm={12} className="ml-n3">
          <h3>{t('documents_send_documents_contacts')}</h3>
        </Col>
      </Row>
    );
  };

  return (
    <ShareDocumentContactsWrapper>
      <Title />
      <div className="w-100 my-3">
        <div className="form-container">
          {selectedClients.map((selectedClient) => (
            <div className="w-100" key={selectedClient.clientId}>
              <div className="w-100 client-name-box mb-3">
                <strong>{selectedClient.clientName}</strong>
              </div>
              <div className="mb-2 d-flex align-items-center">
                <span className="document-select-all-client">
                  {t('documents_select_all_client')}
                </span>
                <Switch
                  className="text-uppercase"
                  id={`switchActiveAll-${selectedClient.clientId}`}
                  color={selectedClient.isActiveAll ? colors.bilbao : colors.error}
                  value={!selectedClient.isActiveAll}
                  onChange={(checked) => {
                    onChangeActiveContactAll(checked, selectedClient);
                  }}
                  classNameMain="ml-1"
                />
              </div>
              {selectedClient.contacts && (
                <div className="contact-box-container mb-4">
                  <div className="mb-3 search-contacts-container">
                    <div className="form search-contacts-form">
                      <SearchContacts
                        Icon={() => null}
                        borderRadius={2}
                        className="mb-4 w-25 m-0 h-50"
                        inputClassName="w-100"
                        buttonClassName=""
                        withButtonClear
                        rightIcon={<SearchIcon fill={colors.black} />}
                        onChange={(e) => onSearchContacts(e, selectedClient)}
                        placeholder={t('client_search_contact')}
                      />
                    </div>
                  </div>
                  <ul className="share-contacts-container">
                    {selectedClient.contacts?.map((contact) => (
                      <li className="mb-3 list-contact share-list-contact" key={contact.mail}>
                        <Checkbox
                          type="checkbox"
                          id="filterCheckAll"
                          label=""
                          checked={selectedContacts.some((file) => file.mail === contact.mail)}
                          onClick={(e) => selectedContact(e, contact, selectedClient)}
                        />
                        <span className="mt-n1">{`${contact.name} ${contact.surname}`}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="d-flex justify-content-end w-100 mt-4">
          <Button
            className="home__btn text-uppercase"
            type="button"
            label={t('general_add')}
            color="primary"
            onClick={() => onCreateDocument(false)}
          />
        </div>
      </div>
      <AlertModal
        type="waiting"
        isOpen={showWaiting}
        toggle={onCancelOperation}
        message={WaitingMessage}
        okText={t('general_button_cancel')}
        onOk={onCancelOperation}
      />
      <AlertModal
        type="success"
        isOpen={showSuccessUpload}
        toggle={onOkSuccessUpload}
        message={t('documents_success_upload_file')}
        okText={t('general_button_ok')}
        onOk={onOkSuccessUpload}
      />
    </ShareDocumentContactsWrapper>
  );
};

ShareDocumentContacts.defaultProps = {
  setSelectedClients: null,
  selectedClients: null,
  selectedFiles: null,
  onClose: null,
};

ShareDocumentContacts.propTypes = {
  setSelectedClients: PropTypes.func,
  selectedClients: PropTypes.arrayOf(PropTypes.shape({})),
  selectedFiles: PropTypes.arrayOf(PropTypes.shape({})),
  onClose: PropTypes.func,
};

export default ShareDocumentContacts;
